import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
  Badge,
} from "reactstrap";
import { Triangle } from "react-loader-spinner";
import toast from "react-hot-toast";
import DataTable from "react-data-table-component";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import DeviceHeartbeatService from "../../../services/DeviceHeartbeatServices/DeviceHeartbeatService";
import POSTerminal from "../../../OrderingSystem/Ads/component/POSTerminal";

// Custom hook for polling device status
const useDeviceStatus = (refreshInterval = 10000) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const intervalRef = useRef(null);

  const fetchStatus = useCallback(async (isInitial = false) => {
    try {
      if (isInitial) setLoading(true);

      const response = await DeviceHeartbeatService.getLatestDeviceStatus();
      const newData = Array.isArray(response) ? response : [];

      setData((prev) => {
        if (JSON.stringify(prev) !== JSON.stringify(newData)) {
          return newData;
        }
        return prev;
      });

      setError(null);
    } catch (err) {
      setError(err.message);
      toast.error("Failed to fetch device status");
      setData([]);
    } finally {
      if (isInitial) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus(true);

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    intervalRef.current = setInterval(() => fetchStatus(false), refreshInterval);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [fetchStatus, refreshInterval]);

  return { data, loading, error, refetch: () => fetchStatus(true) };
};

// Format date time nicely
const formatDateTime = (dateString) => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  return date.toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
};

// Calculate time difference from now
const getTimeDifference = (dateString) => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffSecs = Math.floor(diffMs / 1000);

  if (diffSecs < 60) return `${diffSecs}s ago`;
  if (diffSecs < 3600) return `${Math.floor(diffSecs / 60)}m ago`;
  if (diffSecs < 86400) return `${Math.floor(diffSecs / 3600)}h ago`;
  return `${Math.floor(diffSecs / 86400)}d ago`;
};

const DeviceViewer = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [deviceFilter, setDeviceFilter] = useState("all"); // all, active, inactive

  const { data, loading, error, refetch } = useDeviceStatus(10000);

  // Apply client-side search and filter
  const filteredData = useMemo(() => {
    return Array.isArray(data)
      ? data.filter((device) => {
          const searchLower = searchTerm.toLowerCase();
          const matchesSearch =
            (device.macId && device.macId.toLowerCase().includes(searchLower)) ||
            (device.machineName &&
              device.machineName.toLowerCase().includes(searchLower)) ||
            (device.applicationName &&
              device.applicationName.toLowerCase().includes(searchLower)) ||
            (device.localIp && device.localIp.toLowerCase().includes(searchLower));

          if (deviceFilter === "active") {
            return matchesSearch && device.isActive === true;
          } else if (deviceFilter === "inactive") {
            return matchesSearch && device.isActive === false;
          }
          return matchesSearch;
        })
      : [];
  }, [data, searchTerm, deviceFilter]);

  // Clear selection when filtered data changes (device might be removed)
  useEffect(() => {
    if (
      selectedDevice &&
      !filteredData.find((d) => d.macId === selectedDevice.macId)
    ) {
      setSelectedDevice(null);
    }
  }, [filteredData, selectedDevice]);

  // Custom styles for DataTable
  const customStyles = {
    headCells: {
      style: {
        fontSize: "0.92rem",
        fontWeight: 610,
        backgroundColor: "#f8f9fa",
      },
    },
    rows: {
      style: {
        fontSize: "0.9rem",
        cursor: "pointer",
        transition: "all 0.2s ease",
      },
      stripedStyle: {
        backgroundColor: "#f8f9fa",
      },
      highlightOnHoverStyle: {
        backgroundColor: "rgba(0, 0, 0, 0.1)",
      },
    },
  };

  // Conditional row styles for green/red highlighting based on active status
  const conditionalRowStyles = [
    {
      when: (row) =>
        selectedDevice && row.macId === selectedDevice.macId,
      style: {
        backgroundColor: "rgba(13, 110, 253, 0.1)",
        borderLeft: "4px solid #0d6efd",
      },
    },
    {
      when: (row) => row.isActive === true,
      style: {
        backgroundColor: "rgba(34, 197, 94, 0.05)",
        color: "#15803d",
        fontWeight: 500,
      },
    },
    {
      when: (row) => row.isActive === false,
      style: {
        backgroundColor: "rgba(239, 68, 68, 0.05)",
        color: "#b91c1c",
        fontWeight: 500,
      },
    },
  ];

  // Memoize columns to prevent DataTable from re-rendering on every state change
  const columns = useMemo(
    () => [
      {
        name: "MAC ID",
        selector: (row) => row.macId || "-",
        sortable: true,
        cell: (row) => <code className="text-dark fw-bold">{row.macId}</code>,
        width: "140px",
      },
      {
        name: "Machine Name",
        selector: (row) => row.machineName || "-",
        sortable: true,
        width: "150px",
      },
      {
        name: "Application",
        selector: (row) => row.applicationName || "-",
        sortable: true,
        width: "120px",
      },
      {
        name: "Status",
        selector: (row) => (row.isActive ? "Active" : "Inactive"),
        sortable: true,
        cell: (row) => (
          <Badge color={row.isActive ? "success" : "danger"}>
            {row.isActive ? (
              <>
                <i className="ri-checkbox-circle-fill"></i> Active
              </>
            ) : (
              <>
                <i className="ri-close-circle-fill"></i> Inactive
              </>
            )}
          </Badge>
        ),
        width: "120px",
      },
    ],
    []
  );

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          {/* Breadcrumb */}
          <BreadCrumb
            title="Device Viewer"
            pageTitle="Device Management"
          />

          <Row>
            {/* Left Column - Device List */}
            <Col lg="5">
              <Card>
                <CardHeader className="d-flex justify-content-between align-items-center mb-3">
                  <h4 className="card-title mb-0">Devices</h4>
                  <button
                    className="btn btn-sm btn-primary"
                    onClick={() => refetch()}
                    disabled={loading}
                  >
                    <i className="ri-refresh-line me-1"></i>
                    {loading ? "Refreshing..." : "Refresh"}
                  </button>
                </CardHeader>

                <CardBody className="pb-2">
                  {/* Search Bar */}
                  <div className="mb-3">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search by MAC ID, Name, App, or IP..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>

                  {/* Filter Buttons */}
                  <div className="mb-3 d-flex gap-2">
                    <button
                      className={`btn btn-sm ${
                        deviceFilter === "all"
                          ? "btn-info"
                          : "btn-outline-info"
                      }`}
                      onClick={() => setDeviceFilter("all")}
                    >
                      All
                    </button>
                    <button
                      className={`btn btn-sm ${
                        deviceFilter === "active"
                          ? "btn-success"
                          : "btn-outline-success"
                      }`}
                      onClick={() => setDeviceFilter("active")}
                    >
                      <i className="ri-checkbox-circle-fill me-1"></i>
                      Active
                    </button>
                    <button
                      className={`btn btn-sm ${
                        deviceFilter === "inactive"
                          ? "btn-danger"
                          : "btn-outline-danger"
                      }`}
                      onClick={() => setDeviceFilter("inactive")}
                    >
                      <i className="ri-close-circle-fill me-1"></i>
                      Inactive
                    </button>
                  </div>

                  {/* Loading State */}
                  {loading && data.length === 0 && (
                    <div className="d-flex justify-content-center align-items-center py-5">
                      <Triangle
                        height="80"
                        width="80"
                        color="#0dcaf0"
                        ariaLabel="triangle-loading"
                        visible={true}
                      />
                    </div>
                  )}

                  {/* Error State */}
                  {error && !loading && (
                    <div className="alert alert-danger mt-3">
                      <i className="ri-error-warning-line me-2"></i>
                      {error}
                    </div>
                  )}

                  {/* No Data State */}
                  {!loading && filteredData.length === 0 && searchTerm && (
                    <div className="alert alert-info mt-3">
                      <i className="ri-information-line me-2"></i>
                      No devices found matching your search criteria.
                    </div>
                  )}

                  {/* DataTable */}
                  {!loading && filteredData.length > 0 && (
                    <div style={{ maxHeight: "600px", overflowY: "auto" }}>
                      <DataTable
                        columns={columns}
                        data={filteredData}
                        persistTableHead
                        conditionalRowStyles={conditionalRowStyles}
                        customStyles={customStyles}
                        striped
                        responsive
                        selectableRows={false}
                        onRowClicked={(row) => setSelectedDevice(row)}
                        pointerOnHover
                      />
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>

            {/* Right Column - Device Terminal Preview */}
            <Col lg="7">
              <Card>
                <CardHeader className="mb-3">
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <h4 className="card-title mb-0">
                        {selectedDevice
                          ? `${selectedDevice.machineName} - Preview`
                          : "Select a Device"}
                      </h4>
                      {selectedDevice && (
                        <small className="text-muted d-block mt-1">
                          <i className="ri-server-line me-1"></i>
                          MAC ID: <code>{selectedDevice.macId}</code>
                        </small>
                      )}
                    </div>
                    {selectedDevice && (
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => setSelectedDevice(null)}
                      >
                        <i className="ri-close-line"></i>
                      </button>
                    )}
                  </div>
                </CardHeader>

                <CardBody>
                  {selectedDevice ? (
                    <div>
                      {/* Device Information */}
                      <div className="mb-4 p-3 bg-light rounded">
                        <div className="row">
                          <div className="col-md-6">
                            <div className="mb-2">
                              <small className="text-muted">Machine Name</small>
                              <p className="fw-bold mb-0">
                                {selectedDevice.machineName || "-"}
                              </p>
                            </div>
                            <div className="mb-2">
                              <small className="text-muted">Application</small>
                              <p className="fw-bold mb-0">
                                {selectedDevice.applicationName || "-"}
                              </p>
                            </div>
                            <div className="mb-2">
                              <small className="text-muted">Version</small>
                              <p className="fw-bold mb-0">
                                <Badge>
                                  {selectedDevice.appVersion || "-"}
                                </Badge>
                              </p>
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="mb-2">
                              <small className="text-muted">Local IP</small>
                              <p className="fw-bold mb-0">
                                <code>{selectedDevice.localIp || "-"}</code>
                              </p>
                            </div>
                            <div className="mb-2">
                              <small className="text-muted">Last Heartbeat</small>
                              <p className="fw-bold mb-0">
                                {formatDateTime(
                                  selectedDevice.heartbeatAt ||
                                    selectedDevice.lastHeartbeat
                                )}
                              </p>
                            </div>
                            <div className="mb-2">
                              <small className="text-muted">Status</small>
                              <p className="fw-bold mb-0">
                                <Badge
                                  color={
                                    selectedDevice.isActive
                                      ? "success"
                                      : "danger"
                                  }
                                >
                                  {selectedDevice.isActive
                                    ? "Active"
                                    : "Inactive"}
                                </Badge>
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* POS Terminal Preview */}
                      <div className="mb-3">
                        <h5 className="mb-3">
                          <i className="ri-layout-2-line me-2"></i>Terminal
                          Preview
                        </h5>
                        <div
                          style={{
                            border: "1px solid #ddd",
                            borderRadius: "8px",
                            padding: "10px",
                            backgroundColor: "#f9f9f9",
                            minHeight: "400px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <POSTerminal />
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-5">
                      <i
                        className="ri-device-line"
                        style={{ fontSize: "3rem", color: "#ccc" }}
                      ></i>
                      <p className="text-muted mt-3">
                        Click on a device from the list to view its details and
                        terminal preview
                      </p>
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default DeviceViewer;
